import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import { ModalConfirmationWithErrorAndLoader } from '../../../common/ModalConfirmationWithErrorAndLoader';
import actions from '../../../../actions';
import { executeHttpRequest } from '../../../../epics/api';
import history from '../../../../history';
import { TAgency, TPageNotification } from '../../../../types';
import { getErrorMessages } from '../../../../utils';

type TDelegateJobContainerProps = {
    jobrequestIds?: number[];
    onCancel?: () => void;
    match?: any;
    notification: TPageNotification;
}

export default function DelegateJobContainer({
    jobrequestIds,
    onCancel,
    match,
    notification,
}: TDelegateJobContainerProps) {
    const dispatch = useDispatch();

    const [agencyOptions, setAgencyOptions] = useState([]);
    const [selectedAgency, setSelectedAgency] = useState(null);
    const [isFetchingAgencies, setIsFetchingAgencies] = useState(false);
    const [agencyError, setAgencyError] = useState(null);

    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isDelegating, setIsDelegating] = useState(false);
    const [delegateError, setDelegateError] = useState(null);

    const jobIds = jobrequestIds || [parseInt(match.params.jobId, 10)];
    const isMassAction = !!jobrequestIds;
    const plural = jobIds.length > 1;

    const fetchAgencyOptions = () => {
        setIsFetchingAgencies(true);
        executeHttpRequest('agencies/', 'get')
            .then(response => {
                setIsFetchingAgencies(false);
                const options = response.data.content
                    .sort((a: TAgency, b: TAgency) => {
                        if (a.company_name < b.company_name) return -1;
                        if (a.company_name > b.company_name) return 1;
                        return 0;
                    })
                    .map((agency: TAgency) => ({
                        value: agency.id,
                        label: agency.company_name,
                    }));
                setAgencyOptions(options);
            })
            .catch(() => {
                setIsFetchingAgencies(false);
                setAgencyError('Failed to fetch agency data. Please try again later.');
            });
    };

    useEffect(() => fetchAgencyOptions(), []);

    const handleCancel = () => isMassAction ? onCancel() : history.goBack();

    const handleContinue = () => {
        setDelegateError(false);
        setIsConfirmModalOpen(true);
    };

    const handleDelegate = () => {
        setIsDelegating(true);
        setDelegateError(null);

        let path;
        let data;
        if (plural) {
            path = 'jobs/delegate/';
            data = { agency: selectedAgency.value, jobrequests: jobIds };
        } else {
            path = `jobs/${jobIds[0]}/delegate/`;
            data = { agency: selectedAgency.value };
        }

        executeHttpRequest(path, 'patch', data)
            .then(response => {
                notification.success('Success', `Job${plural ? 's' : ''} successfully delegated`);
                if (plural) {
                    // Close the modal and reload the shifts list
                    const currentUrlParams = new URLSearchParams(history.location.search);
                    dispatch(actions.loadShifts(currentUrlParams));
                } else {
                    // Redirect to the shifts list
                    const seriesId = response.data.content.series;
                    const searchParam = seriesId ? `?series=${seriesId}` : `?search=JR${jobIds[0]}`;
                    const targetUrlParams = `${searchParam}&getall=1`;
                    history.push({ pathname: '/jobs/list/', search: targetUrlParams });
                    // Reload the shifts list
                    dispatch(actions.loadShifts(new URLSearchParams(targetUrlParams)));
                }
                if (isMassAction) {
                    handleCancel();
                }
            })
            .catch(err => {
                setIsDelegating(false);
                setDelegateError(getErrorMessages(err));
            });
    };

    return <ModalConfirmationWithErrorAndLoader
        className={null}
        errorMessage={agencyError}
        handleCancel={handleCancel}
        handleContinue={handleContinue}
        isError={!!agencyError}
        isInProgress={isFetchingAgencies}
        setIsInProgress={setIsFetchingAgencies}
        isOpen={true}
        title="Delegate Job"
        continueEnabled={!!selectedAgency}
        continueButtonText="CONTINUE"
    >
        <p className="modal-title">Delegate Job{plural && 's'}</p>
        <p className="margin-bottom-20">Please select the branch that you want to delegate to:</p>
        <Select
            options={agencyOptions}
            value={selectedAgency}
            onChange={setSelectedAgency}
            placeholder="Select branch..."
            clearable={false}
        />

        {/* Confirmation modal */}
        <ModalConfirmationWithErrorAndLoader
            className="confirm-delegate-modal"
            errorMessage={delegateError}
            handleCancel={() => setIsConfirmModalOpen(false)}
            handleContinue={handleDelegate}
            isError={!!delegateError}
            isInProgress={isDelegating}
            setIsInProgress={setIsDelegating}
            isOpen={isConfirmModalOpen}
            title="Confirm Delegate Job"
            continueButtonText="CONFIRM"
        >
            <p className="modal-title">Confirm Delegate Job{plural && 's'}</p>
            <p>
                Are you sure you want to delegate the following job{plural && 's'} to{' '}
                <strong>{selectedAgency?.label}</strong>?
            </p>
            <p className="margin-top-10"><strong>{jobIds.join(', ')}</strong></p>
            <p className="margin-top-10">
                <strong>Please note:</strong> If{' '}
                {plural ? 'these jobs are part of ongoing jobs' : 'this job is part of an ongoing job'},
                the delegation will apply to all future scheduled dates.
            </p>
        </ModalConfirmationWithErrorAndLoader>
    </ModalConfirmationWithErrorAndLoader>;
}
